import { useState, useEffect } from "react";
/* ================= SETTINGS PAGE ================= */

function Settings({ settings, setSettings, sidebarTheme }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 480
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div>
      <h1 style={{ fontSize: 32, marginBottom: 20 }}>⚙ Settings</h1>

      {/* THEME */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <div>
          <strong>Theme</strong>
          <div style={settingsStyles(sidebarTheme, isMobile).settingSub}></div>
        </div>
        <Segment
          value={settings.theme}
          options={["light", "dark", "system"]}
          theme={sidebarTheme}
          onChange={(v) => setSettings({ ...settings, theme: v })}
        />
      </div>

      {/* ANSWER DIRECTION */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <div>
          <strong>Answer Direction</strong>
          <div style={settingsStyles(sidebarTheme, isMobile).settingSub}></div>
        </div>
        <Segment
          value={settings.direction}
          options={["left to right", "right to left"]}
          theme={sidebarTheme}
          onChange={(v) => setSettings({ ...settings, direction: v })}
        />
      </div>

      {/* SHOW PROBLEM NUMBER */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <strong>Show Problem Number</strong>
        <Toggle
          value={settings.showNumber}
          theme={sidebarTheme}
          onChange={() =>
            setSettings({ ...settings, showNumber: !settings.showNumber })
          }
        />
      </div>

      {/* SHOW TIMER */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <strong>Show Timer</strong>
        <Toggle
          value={settings.showTimer}
          theme={sidebarTheme}
          onChange={() =>
            setSettings({ ...settings, showTimer: !settings.showTimer })
          }
        />
      </div>

      {/* TIMER MODE */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <div>
          <strong>Timer Mode</strong>
          <div style={settingsStyles(sidebarTheme, isMobile).settingSub}></div>
        </div>
        <Segment
          value={settings.timerMode}
          options={["problem", "total"]}
          theme={sidebarTheme}
          onChange={(v) => setSettings({ ...settings, timerMode: v })}
        />
      </div>

      {/* SHOW ABORT */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <strong>Show Abort Button</strong>
        <Toggle
          value={settings.showAbort}
          theme={sidebarTheme}
          onChange={() =>
            setSettings({ ...settings, showAbort: !settings.showAbort })
          }
        />
      </div>

      {/* SHOW KEYPAD */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <strong>Show On-Screen Keypad</strong>
        <Toggle
          value={settings.showKeypad}
          theme={sidebarTheme}
          onChange={() =>
            setSettings({ ...settings, showKeypad: !settings.showKeypad })
          }
        />
      </div>

      {/* STREAM SPEED */}
      <div style={settingsStyles(sidebarTheme, isMobile).settingRow}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <strong>Stream Speed</strong>
          <div style={settingsStyles(sidebarTheme, isMobile).settingSub}>
            Easy is user-defined • Medium & Hard auto
            <br />
            Set the time gap for number display in Easy mode.
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gap: 8,
            gridTemplateColumns: isMobile ? "1fr" : "auto auto auto auto",
            gridAutoFlow: isMobile ? "row" : "column",

            alignItems: "center",
          }}
        >
          {/* RULE SELECT (NO "c" SHOWN) */}
          <select
            value={settings.streamSpeed?.c ?? 2}
            onChange={(e) =>
              setSettings({
                ...settings,
                streamSpeed: {
                  ...settings.streamSpeed,
                  c: Number(e.target.value),
                },
              })
            }
            style={{
              gridColumn: isMobile ? "1 / -1" : "auto",
              height: isMobile ? 38 : 44,
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
              padding: isMobile ? "0 10px" : "0 12px",
              width: isMobile ? "100%" : "auto", // ✅ IMPORTANT
              borderRadius: 12,
              padding: isMobile ? "0 10px" : "0 12px",
              background: sidebarTheme.card,
              color: sidebarTheme.text,
              border: `1px solid ${sidebarTheme.border}`,
            }}
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                Difficulty Rule {n}
              </option>
            ))}
          </select>
          {/* EASY */}
          <SpeedCell
            label="Easy"
            value={`${settings.streamSpeed?.easySec ?? 2} sec`}
            editable
            onChange={(v) =>
              setSettings({
                ...settings,
                streamSpeed: {
                  ...settings.streamSpeed,
                  easySec: Math.max(1, Number(v)),
                },
              })
            }
            theme={sidebarTheme}
            isMobile={isMobile}
          />

          {/* MEDIUM */}
          <SpeedCell
            label="Medium"
            value={`${Math.round(
              ((settings.streamSpeed?.easySec ?? 2) * 1000) /
                (settings.streamSpeed?.c ?? 2)
            )} ms`}
            locked
            theme={sidebarTheme}
            isMobile={isMobile}
          />

          {/* HARD */}
          <SpeedCell
            label="Hard"
            value={`${Math.round(
              ((settings.streamSpeed?.easySec ?? 2) * 1000) /
                ((settings.streamSpeed?.c ?? 2) * 2)
            )} ms`}
            locked
            theme={sidebarTheme}
            isMobile={isMobile}
          />
        </div>
      </div>

      <div
        style={{
          fontSize: 12,
          opacity: 0.65,
          marginBottom: 14,
          marginLeft: 6,
        }}
      >
        Medium = Easy ÷ Rule Hard = Easy ÷ (Rule × 2) Lower time means faster
        numbers. All values are in milliseconds.
      </div>

      {/* RESET */}
      <button
        style={{
          ...settingsStyles(sidebarTheme, isMobile).resetDefault,
          background: "#ef4444",
          color: "#fff",
          borderRadius: 10,
          border: "none",
          padding: "10px 14px",
          fontWeight: 900,
          cursor: "pointer",
        }}
        onClick={() =>
          setSettings({
            theme: "light",
            direction: "left to right",
            showNumber: true,
            showTimer: true,
            timerMode: "problem",
            showAbort: false,
            showKeypad: false,
            streamSpeed: {
              easySec: 2,
              c: 2,
            },
          })
        }
      >
        Reset to Default
      </button>
    </div>
  );
}

/* ================= STYLES ================= */

const settingsStyles = (theme, isMobile) => ({
  settingRow: {
    padding: "14px 16px",
    borderRadius: 14,
    display: "flex",
    justifyContent: "space-between",
    alignItems: isMobile ? "stretch" : "center",
    marginBottom: 10,
    background: theme.card,
    border: `1px solid ${theme.border}`,
  },
  settingSub: {
    fontSize: 12,
    opacity: 0.6,
  },
  resetDefault: {
    marginTop: 10,
  },
});

/* ================= HELPERS ================= */

const Toggle = ({ value, onChange, theme }) => (
  <div
    onClick={onChange}
    style={{
      width: 46,
      height: 26,
      borderRadius: 26,
      background: value ? theme.accentGradient : theme.border,
      position: "relative",
      cursor: "pointer",
      transition: "all 0.25s ease",
      boxShadow: value ? "0 0 10px rgba(124,58,237,0.45)" : "none",
    }}
  >
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "#fff",
        position: "absolute",
        top: 3,
        left: value ? 23 : 3,
        transition: "all 0.25s ease",
      }}
    />
  </div>
);

const Segment = ({ value, options, onChange, theme }) => (
  <div
    style={{
      display: "flex",
      background: theme.card,
      borderRadius: 12,
      overflow: "hidden",
      border: `1px solid ${theme.border}`,
    }}
  >
    {options.map((opt) => (
      <div
        key={opt}
        onClick={() => onChange(opt)}
        style={{
          padding: "6px 14px",
          cursor: "pointer",
          fontSize: 13,
          background: value === opt ? theme.accentGradient : "transparent",
          color: value === opt ? "#fff" : theme.text,
          transition: "0.2s",
        }}
      >
        {opt}
      </div>
    ))}
  </div>
);
const SpeedCell = ({
  label,
  value,
  editable,
  onChange,
  locked,
  theme,
  isMobile,
}) => (
  <div
    style={{
      minWidth: isMobile ? 0 : 90,
      height: isMobile ? 38 : 42,
      borderRadius: 12,
      border: `1px solid ${theme.border}`,
      background: theme.card,
      padding: isMobile ? "4px 6px" : "6px 10px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      opacity: locked ? 0.6 : 1,
    }}
  >
    <div style={{ fontSize: isMobile ? 10 : 11, opacity: 0.7 }}>{label}</div>

    {editable ? (
      <input
        type="number"
        min={1}
        step={1}
        value={value.replace(" sec", "")}
        onChange={(e) => onChange(e.target.value)}
        style={{
          border: "none",
          outline: "none",
          background: "transparent",
          color: theme.text,
          fontWeight: 700,
          fontSize: 14,
        }}
      />
    ) : (
      <div style={{ fontWeight: 700, fontSize: 14 }}>{value}</div>
    )}
  </div>
);

export default Settings;
